import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import {
  AssignedProduct,
  FreightProduct,
  HandlingUnit,
} from "./freight-booking-types";

interface UnassignedProduct {
  product: FreightProduct;
  unassignedQuantity: number;
}

function getUnassignedProducts(
  products: FreightProduct[],
  handlingUnits: HandlingUnit[],
  currentUnitIndex: number,
): UnassignedProduct[] {
  const assignedQuantities = new Map<string, number>();
  for (const [i, handlingUnit] of handlingUnits.entries()) {
    if (i === currentUnitIndex) {
      continue;
    }
    for (const ap of handlingUnit.assignedProducts) {
      assignedQuantities.set(
        ap.productId,
        (assignedQuantities.get(ap.productId) ?? 0) + ap.quantity,
      );
    }
  }

  const result: UnassignedProduct[] = [];
  for (const product of products) {
    const assignedElsewhere = assignedQuantities.get(product.id) ?? 0;
    const unassigned = product.quantity - assignedElsewhere;
    if (unassigned > 0) {
      result.push({ product, unassignedQuantity: unassigned });
    }
  }
  return result;
}

interface AssignProductsModalProps {
  isOpen: boolean;
  onClose(): void;
  onAssign(assignedProducts: AssignedProduct[]): void;
  products: FreightProduct[];
  handlingUnits: HandlingUnit[];
  unitIndex: number;
}

export const AssignProductsModal = memo(function AssignProductsModal({
  isOpen,
  onClose,
  onAssign,
  products,
  handlingUnits,
  unitIndex,
}: AssignProductsModalProps) {
  const unassignedProducts = useMemo(
    () => getUnassignedProducts(products, handlingUnits, unitIndex),
    [products, handlingUnits, unitIndex],
  );

  const [quantities, setQuantities] = useState<Map<string, number>>(() => {
    const initial = new Map<string, number>();
    const currentUnit = handlingUnits[unitIndex];
    if (currentUnit) {
      for (const ap of currentUnit.assignedProducts) {
        initial.set(ap.productId, ap.quantity);
      }
    }
    return initial;
  });

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    setQuantities((prev) => {
      const next = new Map(prev);
      if (quantity === 0) {
        next.delete(productId);
      } else {
        next.set(productId, quantity);
      }
      return next;
    });
  }, []);

  const hasSelections = quantities.size > 0;

  const handleAssign = useCallback(() => {
    const assigned: AssignedProduct[] = [];
    for (const [productId, quantity] of quantities) {
      if (quantity > 0) {
        assigned.push({ productId, quantity });
      }
    }

    onAssign(assigned);
    setQuantities(new Map());
  }, [quantities, onAssign]);

  const handleClose = useCallback(() => {
    setQuantities(new Map());
    onClose();
  }, [onClose]);

  const handleToggleAll = useCallback(() => {
    if (hasSelections) {
      setQuantities(new Map());
    } else {
      const next = new Map<string, number>();
      for (const up of unassignedProducts) {
        next.set(up.product.id, up.unassignedQuantity);
      }
      setQuantities(next);
    }
  }, [hasSelections, unassignedProducts]);

  return (
    <RedoModal
      additionalFooterContent={
        unassignedProducts.length > 0 ? (
          <RedoButton
            hierarchy="link"
            onClick={handleToggleAll}
            size="sm"
            text={hasSelections ? "Unassign all" : "Assign all"}
          />
        ) : undefined
      }
      isOpen={isOpen}
      modalSize="sm"
      onModalCloseRequested={handleClose}
      primaryButton={{
        text: "Assign products",
        size: "sm",
        onClick: handleAssign,
        disabled: !hasSelections,
      }}
      secondaryButton={{ text: "Cancel", onClick: handleClose, size: "sm" }}
      title="Assign products"
    >
      {unassignedProducts.length === 0 ? (
        <Flex align="center" justify="center" p="xl">
          <Text fontSize="sm" textColor="secondary">
            All products have been assigned.
          </Text>
        </Flex>
      ) : (
        <Flex dir="column" gap="lg">
          {unassignedProducts.map((up) => (
            <Flex align="center" justify="space-between" key={up.product.id}>
              <Flex dir="column" gap="xxs">
                <Text fontSize="xs" fontWeight="medium" textColor="primary">
                  {up.product.name}
                  {up.product.sku ? ` - ${up.product.sku}` : ""}
                </Text>
                <Text fontSize="xxs" textColor="secondary">
                  {up.unassignedQuantity} available
                </Text>
              </Flex>
              <RedoIncrementDecrement
                max={up.unassignedQuantity}
                min={0}
                setValue={(val) => updateQuantity(up.product.id, val)}
                size="small"
                value={quantities.get(up.product.id) ?? 0}
              />
            </Flex>
          ))}
        </Flex>
      )}
    </RedoModal>
  );
});
